/**
 * UpdatePrompt — card "Nova versão disponível" (Story X1.1.2).
 *
 * Fica montado globalmente no shell autenticado (App.tsx) e se auto-gate: só
 * renderiza quando o Service Worker detecta um deploy novo em espera
 * (useServiceWorkerUpdate → evento `waiting` do workbox-window).
 *
 * "Atualizar" chama applyUpdate(): envia SKIP_WAITING ao SW novo e, quando ele
 * assume o controle, a página recarrega na versão nova (sem hard-refresh manual).
 * "Agora não" só esconde o card nesta sessão — o SW novo continua em espera e o
 * prompt reaparece no próximo carregamento até o usuário atualizar.
 *
 * Posicionamento espelha NotificationPrompt/InstallBanner: card flutuante no canto
 * inferior. Usa o offset `bottom-[9rem]` no mobile para empilhar acima dos outros
 * prompts no caso raro de coexistirem.
 */
import { useState } from "react";
import { RefreshCw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";

export function UpdatePrompt() {
  const { updateReady, applyUpdate } = useServiceWorkerUpdate();
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);

  if (!updateReady || dismissed) return null;

  const handleUpdate = () => {
    setUpdating(true);
    // applyUpdate dispara SKIP_WAITING; o reload vem do listener `controlling`.
    applyUpdate();
  };

  return (
    <div
      role="dialog"
      aria-label="Atualização disponível"
      data-testid="update-prompt"
      className="fixed inset-x-4 bottom-[9rem] z-50 rounded-lg border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:inset-x-auto sm:bottom-4 sm:left-4 sm:w-96"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <RefreshCw className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Nova versão disponível</p>
          <p className="text-xs text-muted-foreground">
            Uma atualização do SE_WEB foi baixada. Atualize para usar a versão mais recente.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleUpdate}
              disabled={updating}
              data-testid="update-prompt-apply"
            >
              {updating ? "Atualizando…" : "Atualizar"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDismissed(true)}
              data-testid="update-prompt-later"
            >
              Agora não
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dispensar"
          data-testid="update-prompt-dismiss"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default UpdatePrompt;
